import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SiteSettings {
  site_name: string;
  site_tagline: string;
  site_description: string;
  contact_email: string;
  support_phone: string;
  maintenance_mode: boolean;
  logo_url: string;
  hero_image_url: string;
  footer_address: string;
  footer_copyright: string;
  footer_facebook: string;
  footer_twitter: string;
  footer_instagram: string;
  [key: string]: any;
}

const defaultSettings: SiteSettings = {
  site_name: 'Nazandoo',
  site_tagline: 'Votre marketplace locale',
  site_description: 'Découvrez les meilleurs produits locaux et internationaux directement à Kinshasa',
  contact_email: 'contact@nazandoo.cd',
  support_phone: '+243 123 456 789',
  maintenance_mode: false,
  logo_url: '',
  hero_image_url: '', // Leave empty to use built-in fallback image
  footer_address: 'Kinshasa, RD Congo',
  footer_copyright: '© 2024 Nazandoo. Tous droits réservés.',
  footer_facebook: 'https://facebook.com',
  footer_twitter: 'https://twitter.com',
  footer_instagram: 'https://instagram.com'
};

export const useSiteSettings = () => {
  const [settings, setSettings] = useState<SiteSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);

  const fetchSettings = useCallback(async () => {
    console.log('[useSiteSettings] fetchSettings called');
    try {
      const { data, error } = await supabase
        .from('site_settings')
        .select('key, value');

      if (error) throw error;

      const settingsObj: SiteSettings = { ...defaultSettings };
      
      data?.forEach((setting) => {
        try {
          const parsedValue = typeof setting.value === 'string' 
            ? JSON.parse(setting.value) 
            : setting.value;
          settingsObj[setting.key] = parsedValue;
        } catch {
          settingsObj[setting.key] = setting.value;
        }
      });

      setSettings(settingsObj);
    } catch (error) {
      console.error('Error fetching site settings:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();

    // Subscribe to real-time updates
    const channel = supabase
      .channel('site_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'site_settings'
        },
        () => {
          fetchSettings();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchSettings]);

  return useMemo(() => ({
    settings,
    loading,
    refetch: fetchSettings
  }), [settings, loading, fetchSettings]);
};
